<script>
  import { format } from 'svelte-i18n';
  import { Button, Card, CardBody, Spinner } from '@sveltestrap/sveltestrap';
  import { format as formatDate } from 'date-fns';

  import { PasswordField, SwitchField, TextField } from '@client/components/fields/standalone';
  import { Icon } from '@client/components/icon';
  import { ScrollableSection } from '@client/components/scrollspy';

  import { disabledValue, enabledValue } from '@client/pages/manage/domain-settings';

  import { IntegrationsService } from '@client/services/integrations';

  export let settings = {};
  export let onUpdate = null;

  let isDisconnecting = false;

  const disconnectOutreach = async (instanceId) => {
    if (settings.outreachRefreshTokens && settings.outreachRefreshTokens[instanceId]) {
      isDisconnecting = instanceId;

      delete settings.outreachRefreshTokens[instanceId];

      settings = { ...settings };

      try {
        await IntegrationsService.disconnectOutreach({ instanceId });
      } catch (e) {
        console.error('Failed to disconnect outreach instance', e);
      }

      if (onUpdate) {
        await onUpdate('outreachRefreshTokens', 'Outreach Refresh Tokens', settings.outreachRefreshTokens);
      }

      isDisconnecting = false;
    }
  };
</script>

<div id="integrations">
  <ScrollableSection id="integrations-calendly">
    <h1 class="mb-3">
      <span>{$format('label.CALENDLY')}</span>
      <Button href="https://login.sendergen.com/auth/calendly" color="secondary" size="sm">
        {$format('label.CONNECT')}
      </Button>
    </h1>

    <Card>
      <CardBody class="p-4 pt-1">
        <SwitchField
          id="calendlyenabled"
          label={$format('label.INTEGRATION')}
          tooltip={$format('label.TOOLTIP.INTEGRATION_CALENDLY')}
          valueOn={$format(enabledValue)}
          valueOff={$format(disabledValue)}
          bind:checked={settings.calendlyenabled}
          {onUpdate}
        >
          <div>
            {#if settings.calendlyOrganization}
              <div class="info-panel">
                <b>{settings.calendlyOrganization}</b>
                {$format('label.CONNECTED_ON')}
                <b>{formatDate(new Date(settings.calendlyConnectTime), 'MMMM d, yyyy')}</b>.
              </div>
            {/if}
          </div>
        </SwitchField>
      </CardBody>
    </Card>
  </ScrollableSection>

  <ScrollableSection id="integrations-drift">
    <h1 class="mb-3">
      <span>{$format('label.DRIFT')}</span>
      <Button href="https://login.sendergen.com/auth/dirft" color="secondary" size="sm"
        >{$format('label.CONNECT')}</Button
      >
    </h1>

    <Card>
      <CardBody class="p-4 pt-1">
        <SwitchField
          id="driftenabled"
          label={$format('label.INTEGRATION')}
          tooltip={$format('label.TOOLTIP.INTEGRATION_DRIFT')}
          valueOn={$format(enabledValue)}
          valueOff={$format(disabledValue)}
          bind:checked={settings.driftenabled}
          {onUpdate}
        />

        <TextField
          id="driftorgid"
          label={$format('label.ORGANIZATION_ID')}
          tooltip={$format('label.TOOLTIP.ORGANIZATION_ID_DRIFT')}
          bind:value={settings.driftorgid}
          {onUpdate}
          allowCopy
        />
      </CardBody>
    </Card>
  </ScrollableSection>

  <ScrollableSection id="integrations-eloqua">
    <h1 class="mb-3">
      <span>{$format('label.ELOQUA')}</span>
      <Button href="https://login.sendergen.com/auth/eloqua" color="secondary" size="sm"
        >{$format('label.CONNECT')}</Button
      >
    </h1>

    <Card>
      <CardBody class="p-4 pt-1">
        <SwitchField
          id="eloquaenabled"
          label={$format('label.FRICTIONLESS_LANDING_PAGES')}
          tooltip={$format('label.TOOLTIP.FRICTIONLESS_LANDING_PAGES_ELOQUA')}
          valueOn={$format(enabledValue)}
          valueOff={$format(disabledValue)}
          bind:checked={settings.eloquaenabled}
          {onUpdate}
        />

        <TextField
          id="eloquasiteid"
          label={$format('label.SITE_ID')}
          tooltip={$format('label.TOOLTIP.SITE_ID_ELOQUA')}
          bind:value={settings.eloquasiteid}
          {onUpdate}
          allowCopy
        />

        <TextField
          id="eloquaformname"
          label={$format('label.FORM_NAME')}
          tooltip={$format('label.TOOLTIP.FORM_NAME_ELOQUA')}
          bind:value={settings.eloquaformname}
          {onUpdate}
          allowCopy
        />
      </CardBody>
    </Card>
  </ScrollableSection>

  <ScrollableSection id="integrations-hubspot">
    <h1 class="mb-3">
      <span>{$format('label.HUBSPOT')}</span>
      <Button href="https://login.sendergen.com/auth/hubspot" color="secondary" size="sm"
        >{$format('label.CONNECT')}</Button
      >
    </h1>

    <Card>
      <CardBody class="p-4 pt-1">
        <SwitchField
          id="hubspotenabled"
          label={$format('label.FRICTIONLESS_LANDING_PAGES')}
          tooltip={$format('label.TOOLTIP.FRICTIONLESS_LANDING_PAGES_HUBSPOT')}
          valueOn={$format(enabledValue)}
          valueOff={$format(disabledValue)}
          bind:checked={settings.hubspotenabled}
          {onUpdate}
        />

        <TextField
          id="hubspotid"
          label={$format('label.HUB_ID')}
          tooltip={$format('label.TOOLTIP.HUB_ID')}
          bind:value={settings.hubspotid}
          {onUpdate}
          allowCopy
        />
      </CardBody>
    </Card>
  </ScrollableSection>

  <ScrollableSection id="integrations-marketo">
    <h1 class="mb-3">
      <span>{$format('label.MARKETO')}</span>
      <Button href="https://login.sendergen.com/auth/marketo" color="secondary" size="sm"
        >{$format('label.CONNECT')}</Button
      >
    </h1>

    <Card>
      <CardBody class="p-4 pt-1">
        <SwitchField
          id="munchkinenabled"
          label={$format('label.FRICTIONLESS_LANDING_PAGES')}
          tooltip={$format('label.TOOLTIP.FRICTIONLESS_LANDING_PAGES_MARKETO')}
          valueOn={$format(enabledValue)}
          valueOff={$format(disabledValue)}
          bind:checked={settings.munchkinenabled}
          {onUpdate}
        />

        <TextField
          id="munchkinid"
          label={$format('label.MUNCHKIN_ID')}
          tooltip={$format('label.TOOLTIP.MUNCHKIN_ID')}
          bind:value={settings.munchkinid}
          {onUpdate}
          allowCopy
        />

        <PasswordField
          id="munchkinkey"
          label={$format('label.MUNCHKIN_KEY')}
          tooltip={$format('label.TOOLTIP.MUNCHKIN_KEY')}
          bind:value={settings.munchkinkey}
          {onUpdate}
          allowCopy
        />
      </CardBody>
    </Card>
  </ScrollableSection>

  <ScrollableSection id="integrations-outreach">
    <h1 class="mb-3">
      <span>{$format('label.OUTREACH')}</span>
      <Button href="https://login.sendergen.com/auth/outreach" color="secondary" size="sm"
        >{$format('label.CONNECT')}</Button
      >
    </h1>

    <Card>
      <CardBody class="p-4 pt-1">
        <SwitchField
          id="outreachenabled"
          label={$format('label.INTEGRATION')}
          tooltip={$format('label.TOOLTIP.INTEGRATION_OUTREACH')}
          valueOn={$format(enabledValue)}
          valueOff={$format(disabledValue)}
          bind:checked={settings.outreachenabled}
          {onUpdate}
        >
          <div>
            {#if settings.outreachenabled && settings.outreachRefreshTokens}
              <div class="info-panel">
                <ul>
                  {#each Object.entries(settings.outreachRefreshTokens) as [key, info]}
                    <li>
                      <div>
                        {@html $format('label.OUTREACH_CONNECTED', {
                          values: {
                            name: info.instanceName,
                            date: formatDate(new Date(info.connectDate), 'MMMM d, yyyy'),
                            user: info.user.email.split('@').shift()
                          }
                        })}
                        <div>Id: {key}</div>
                      </div>
                      <Button color="secondary" size="sm" on:click={() => disconnectOutreach(key)}>
                        {#if isDisconnecting === key}
                          <Spinner inline size="sm" color="primary" />
                        {:else}
                          {$format('label.DISCONNECT')}
                        {/if}
                      </Button>
                    </li>
                  {/each}
                </ul>
              </div>
            {/if}
          </div></SwitchField
        >
      </CardBody>
    </Card>
  </ScrollableSection>

  <ScrollableSection id="integrations-pardot">
    <h1 class="mb-3">
      <span>{$format('label.PARDOT')}</span>
      <Button href="https://accounts.opensense.com/auth/pardot" color="secondary" size="sm"
        >{$format('label.CONNECT')}</Button
      >
    </h1>

    <Card>
      <CardBody class="p-4 pt-1">
        <SwitchField
          id="pardotenabled"
          label={$format('label.INTEGRATION')}
          tooltip={$format('label.TOOLTIP.INTEGRATION_PARDOT')}
          valueOn={$format(enabledValue)}
          valueOff={$format(disabledValue)}
          bind:checked={settings.pardotenabled}
          {onUpdate}
        />

        <TextField
          id="pardotaid"
          label={$format('label.AID')}
          tooltip={$format('label.TOOLTIP.AID_PARDOT')}
          bind:value={settings.pardotaid}
          {onUpdate}
          allowCopy
        />

        <TextField
          id="pardotcid"
          label={$format('label.CLIENT_ID')}
          tooltip={$format('label.TOOLTIP.CLIENT_ID_PARDOT')}
          bind:value={settings.pardotcid}
          {onUpdate}
          allowCopy
        />
      </CardBody>
    </Card>
  </ScrollableSection>

  <ScrollableSection id="integrations-rollworks">
    <h1 class="mb-3">
      <span>{$format('label.ROLLWORKS')}</span>
      <Button href="https://login.sendergen.com/auth/rollworks" color="secondary" size="sm"
        >{$format('label.CONNECT')}</Button
      >
    </h1>

    <Card>
      <CardBody class="p-4 pt-1">
        <SwitchField
          id="rollworksenabled"
          label={$format('label.INTEGRATION')}
          tooltip={$format('label.TOOLTIP.INTEGRATION_ROLLWORKS')}
          valueOn={$format(enabledValue)}
          valueOff={$format(disabledValue)}
          bind:checked={settings.rollworksenabled}
          {onUpdate}
        />
      </CardBody>
    </Card>
  </ScrollableSection>

  <ScrollableSection id="integrations-salesloft">
    <h1 class="mb-3">
      <span>{$format('label.SALESLOFT')}</span>
      <Button href="https://login.sendergen.com/auth/salesloft" color="secondary" size="sm"
        >{$format('label.CONNECT')}</Button
      >
    </h1>

    <Card>
      <CardBody class="p-4 pt-1">
        <SwitchField
          id="salesloftenabled"
          label={$format('label.INTEGRATION')}
          tooltip={$format('label.TOOLTIP.INTEGRATION_SALESLOFT')}
          valueOn={$format(enabledValue)}
          valueOff={$format(disabledValue)}
          bind:checked={settings.salesloftenabled}
          {onUpdate}
        />
      </CardBody>
    </Card>
  </ScrollableSection>

  <ScrollableSection id="integrations-ukg">
    <h1 class="mb-3">
      <span>{$format('label.UKG')}</span>
    </h1>

    <Card>
      <CardBody class="p-4 pt-1">
        <TextField
          id="ukginstance"
          label={$format('label.UKG_INSTANCE')}
          tooltip={$format('label.TOOLTIP.UKG_INSTANCE')}
          bind:value={settings.ukginstance}
          {onUpdate}
        />

        <PasswordField
          id="ukgcustomerapikey"
          label={$format('label.UKG_API_KEY')}
          tooltip={$format('label.TOOLTIP.UKG_API_KEY')}
          bind:value={settings.ukgcustomerapikey}
          {onUpdate}
        />

        <TextField
          id="ukgusername"
          label={$format('label.UKG_USERNAME')}
          tooltip={$format('label.TOOLTIP.UKG_USERNAME')}
          bind:value={settings.ukgusername}
          {onUpdate}
        />

        <PasswordField
          id="ukgpassword"
          label={$format('label.UKG_PASSWORD')}
          tooltip={$format('label.TOOLTIP.UKG_PASSWORD')}
          bind:value={settings.ukgpassword}
          {onUpdate}
        />
      </CardBody>
    </Card>
  </ScrollableSection>

  <ScrollableSection id="integrations-zapier">
    <h1 class="mb-3">
      <span>{$format('label.ZAPIER')}</span>
      <Button href="https://login.sendergen.com/auth/zapier" color="secondary" size="sm"
        >{$format('label.CONNECT')}</Button
      >
    </h1>

    <Card>
      <CardBody class="p-4 pt-1">
        <TextField
          id="zapierkey"
          label={$format('label.API_KEY')}
          tooltip={$format('label.TOOLTIP.API_KEY_ZAPIER')}
          bind:value={settings.zapierkey}
          {onUpdate}
        />
      </CardBody>
    </Card>
  </ScrollableSection>
</div>

<style lang="scss" src="./integrations.scss"></style>
